import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import CustomTooltip from "../../CustomTooltip";

const SavedFilterChip = ({
  filter,
  onApply,
  onEdit,
  onDelete,
  onUnselect,
  isActive = false,
}) => {
  const colors = useColors();
  const [anchorEl, setAnchorEl] = useState(null);
  const { trackEvent } = useAPI();

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    trackEvent("User clicked on saved filter menu");
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    trackEvent("User closed saved filter menu");
  };

  const handleEdit = () => {
    onEdit(filter);
    handleMenuClose();
    trackEvent("User edited saved filter");
  };

  const handleDelete = () => {
    onDelete(filter);
    handleMenuClose();
    trackEvent("User deleted saved filter");
  };

  const handleChipClick = () => {
    if (!isActive) {
      onApply(filter);
      trackEvent("User clicked on saved filter chip");
    }
  };

  const handleUnselect = (event) => {
    event.stopPropagation();
    if (onUnselect) {
      onUnselect(filter);
      trackEvent("User unselected saved filter");
    }
  };

  return (
    <Box position="relative">
      <CustomTooltip
        title={filter.description || filter.name}
        placement="top"
        arrow
      >
        <Chip
          onClick={handleChipClick}
          onDelete={isActive ? handleUnselect : undefined}
          sx={{
            background: colors.grey_50,
            cursor: isActive ? "default" : "pointer",
            height: "32px",
            width: "min-content",
            border: isActive
              ? `1px solid ${colors.primary_400}`
              : `1px solid ${colors.grey_32percent}`,
            borderRadius: "10px",
            justifyContent: "space-evenly",
            "&:hover": !isActive
              ? {
                  backgroundColor: colors.grey_8percent,
                }
              : {},
            "& .MuiChip-deleteIcon": {
              display: isActive ? "inline-block" : "none",
            },
          }}
          label={
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography
                variant={isActive ? "overline" : "caption"}
                color={colors.primary_400}
                sx={{
                  maxWidth: "120px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {filter.name}
              </Typography>
              {!isActive && (
                <IconButton
                  className="menu-icon"
                  size="small"
                  onClick={handleMenuClick}
                  sx={{
                    padding: "2px 0px 2px 6px",
                    minWidth: "20px",
                    opacity: 1,
                    transition: "opacity 0.2s",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <MoreVertIcon
                    sx={{
                      fontSize: "14px",
                      color: colors.text_secondary,
                      ":hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  />
                </IconButton>
              )}
            </Box>
          }
          deleteIcon={
            isActive ? (
              <CancelRoundedIcon
                style={{
                  width: "16px",
                  color: colors.primary_48percent,
                  cursor: "pointer",
                }}
              />
            ) : null
          }
        />
      </CustomTooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          "& .MuiMenu-list": {
            padding: "4px 0",
          },
        }}
        PaperProps={{
          style: {
            borderRadius: "8px",
            padding: " 0px 4px",
            minWidth: "120px",
            boxShadow: "0px 2px 8px 0px " + colors.grey_48percent,
          },
        }}
      >
        <MenuItem
          onClick={handleEdit}
          sx={{
            color: colors.text_primary,
            "&:hover": {
              backgroundColor: colors.grey_8percent,
            },
          }}
        >
          <Typography variant="caption">Edit filter</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          sx={{
            color: colors.error,
            "&:hover": {
              backgroundColor: colors.error_background,
            },
          }}
        >
          <Typography variant="caption">Delete filter</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

const SavedFiltersRow = ({
  savedFilters = [],
  onApplyFilter,
  onEditFilter,
  onDeleteFilter,
  onUnselectFilter,
  activeFilterId = null,
}) => {
  const colors = useColors();

  if (!savedFilters || savedFilters.length === 0) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography
        variant="caption"
        sx={{
          color: colors.text_secondary,
        }}
      >
        Saved filters
      </Typography>
      <Box display="flex" gap={1} flexWrap="wrap">
        {savedFilters.map((filter) => (
          <SavedFilterChip
            key={filter.id}
            filter={filter}
            onApply={onApplyFilter}
            onEdit={onEditFilter}
            onDelete={onDeleteFilter}
            onUnselect={onUnselectFilter}
            isActive={activeFilterId === filter.id}
          />
        ))}
      </Box>
      <Divider
        sx={{
          margin: "4px 0 0 0",
        }}
      />
    </Box>
  );
};

export default SavedFiltersRow;
